import protobuf from 'protobufjs';

export const UUIDs = {
  mapService: '12345678-1234-5678-1234-56789abcdef0',
  mapCharacteristic: '12345678-1234-5678-1234-56789abcdef1',
  mapRequestCharacteristic: '12345678-1234-5678-1234-56789abcdef2',
  mapCountCharacteristic: '12345678-1234-5678-1234-56789abcdef3',
  compassService: '19b10000-e8f2-537e-4f6c-d104768a1214',
  compassState: '19b10001-e8f2-537e-4f6c-d104768a1214',
  deviceConfig: '55c3d0bf-d6d5-47f5-9222-650e7249b6f6',
  calibrationControl: '3c9d3165-a6ef-42c6-9956-01827683c3d6',
  fixAngleControl: '804dc787-2ca3-45b8-92c1-9e2a0f2baf19',
  calibrationData: '8a257ab3-65e9-4647-98ab-d07fc9bc78b0',
  protobufService: '4e0d6d91-b59c-4951-ab07-203acecebe39',
  protbufSchemaCharacteristic: "91fd85bb-8d4b-4e4a-9f37-74b8c3644cdb",
  nrfBlinkyServce: '00001523-1212-efde-1523-785feabcd123',
  nrfBlinkyLedCharacteristic: '00001525-1212-efde-1523-785feabcd123',
  nrfBlinkyButtonCharacteristic: '00001524-1212-efde-1523-785feabcd123',
};

let device = null;
let mapService = null;
let compassService = null;
let protobufRoot = null;
// eslint-disable-next-line no-unused-vars -- AUD-DEP-13-LINT: write-only binding (assigned later, never read). Deleting it is a behaviour change in the BLE layer, not a lint fix - audit separately.
let calibrationControlCharacteristic = null;
// eslint-disable-next-line no-unused-vars -- AUD-DEP-13-LINT: write-only binding (assigned later, never read). Deleting it is a behaviour change in the BLE layer, not a lint fix - audit separately.
let fixAngleControlCharacteristic = null;
let calibrationDataCharacteristic = null;

export async function connectToDevice(serviceUUID = null) {
  if (!navigator.bluetooth) {
    throw new Error('Bluetooth not supported on this browser.');
  }

  if (!protobufRoot) {
    protobufRoot = await protobuf.load('/compassData.proto');
  }

  device = await navigator.bluetooth.requestDevice({
    filters: [{ services: [serviceUUID || UUIDs.compassService] }],
    optionalServices: [UUIDs.mapService, UUIDs.compassService]
  });

  const server = await device.gatt.connect();
  if(!serviceUUID){ 
    // This is a hack - if serviceUUID is not provided, we assume we are connecting to the compass
    mapService = await server.getPrimaryService(UUIDs.mapService);
    compassService = await server.getPrimaryService(UUIDs.compassService);
    calibrationControlCharacteristic = await compassService.getCharacteristic(UUIDs.calibrationControl);
    fixAngleControlCharacteristic = await compassService.getCharacteristic(UUIDs.fixAngleControl);
    calibrationDataCharacteristic = await compassService.getCharacteristic(UUIDs.calibrationData);
  }
  return device;
}

export function getDevice() {
  return device;
}

async function getCharacteristic(service, characteristicUUID) {
  return await service.getCharacteristic(characteristicUUID);
}

async function readValue(characteristicUUID, service) {
  const characteristic = await getCharacteristic(service, characteristicUUID);
  const value = await characteristic.readValue();
  return new Uint8Array(value.buffer);
}

async function readAndDecode(characteristicUUID, service, protobufType) {
  const buffer = await readValue(characteristicUUID, service);
  return protobufRoot.lookupType(protobufType).decode(buffer);
}

async function writeValue(characteristicUUID, service, value) {
  const characteristic = await getCharacteristic(service, characteristicUUID);
  await characteristic.writeValue(value);
}

async function writeInteger(characteristicUUID, service, value) {
  const buffer = new Uint8Array([value & 0xFF, (value >> 8) & 0xFF]);
  await writeValue(characteristicUUID, service, buffer);
}

async function encodeAndWrite(characteristicUUID, service, protobufType, data) {
  const type = protobufRoot.lookupType(protobufType);
  const buffer = type.encode(data).finish();
  await writeValue(characteristicUUID, service, buffer);
}

export async function requestMapData(startCallback, progressCallback, completeCallback) {
  try {
    console.log('Reading map');
    await getCharacteristic(mapService, UUIDs.mapRequestCharacteristic);

    // Request the number of points
    await writeInteger(UUIDs.mapRequestCharacteristic, mapService, 0xFFFF);

    // Read the number of points
    const countBuffer = await readValue(UUIDs.mapCountCharacteristic, mapService);
    const numPoints = new DataView(countBuffer.buffer).getUint16(0, true);
    console.log('Number of map points:', numPoints);

    if (typeof startCallback === 'function') {
      startCallback(numPoints);
    }

    const mapPoints = [];

    for (let i = 0; i < numPoints; i++) {
      await writeInteger(UUIDs.mapRequestCharacteristic, mapService, i);

      const point = await readAndDecode(UUIDs.mapCharacteristic, mapService, 'compass.MapPoint');
      mapPoints.push(point);

      if (typeof progressCallback === 'function') {
        progressCallback(i + 1, numPoints, point);
      }
    }

    if (typeof completeCallback === 'function') {
      completeCallback(mapPoints);
    }
    console.log('All map points received:', mapPoints);
  } catch (error) {
    console.error('Error in requestMapData:', error);
    throw error;
  }
}

export async function uploadMapData(mapPoints, progressCallback) {
  try {
    console.log('Uploading map');

    // Notify the number of points
    await writeInteger(UUIDs.mapCountCharacteristic, mapService, mapPoints.length);

    for (let i = 0; i < mapPoints.length; i++) {
      let point = mapPoints[i];

      // Remove all emojis from the name
      //point.name = point.name.replace(/[\p{Emoji_Presentation}\p{Emoji}\u200D]+/gu, '');


      // Slice the name to the first 16 bytes
      let byteLength = 0;
      let slicedName = '';

      for (let char of point.name) {
        let charBytes = new TextEncoder().encode(char).length;
        if (byteLength + charBytes > 16) break;
        slicedName += char;
        byteLength += charBytes;
      }

      point.name = slicedName;

      //point.name = point.name.slice(0, 16);
      await encodeAndWrite(UUIDs.mapCharacteristic, mapService, 'compass.MapPoint', point);

      if (typeof progressCallback === 'function') {
        progressCallback(i + 1, mapPoints.length);
      }
    }

    console.log('All map points uploaded successfully');
  } catch (error) {
    console.error('Error in uploadMapData:', error);
    throw error;
  }
}

export async function readCompassState() {
  try {
    return await readAndDecode(UUIDs.compassState, compassService, 'compass.CompassState');
  } catch (error) {
    console.error('Error in readCompassState:', error);
    throw error;
  }
}

export async function subscribeToCompassState(callback) {
  try {
    const characteristic = await getCharacteristic(compassService, UUIDs.compassState);
    await characteristic.startNotifications();

    characteristic.addEventListener('characteristicvaluechanged', async (event) => {
      const buffer = new Uint8Array(event.target.value.buffer);
      const CompassState = protobufRoot.lookupType('compass.CompassState');
      callback(CompassState.decode(buffer));
    });
  } catch (error) {
    console.error('Error in subscribeToCompassState:', error);
    throw error;
  }
}

export async function readDeviceConfig() {
  try {
    return await readAndDecode(UUIDs.deviceConfig, compassService, 'compass.DeviceConfig');
  } catch (error) {
    console.error('Error in readDeviceConfig:', error);
    throw error;
  }
}

export async function writeDeviceConfig(deviceConfig) {
  try {
    await encodeAndWrite(UUIDs.deviceConfig, compassService, 'compass.DeviceConfig', deviceConfig);
  } catch (error) {
    console.error('Error in writeDeviceConfig:', error);
    throw error;
  }
}

export async function startCalibration(angle, dataCallback) {
  try {
    console.log(`ble.js - Starting calibration with angle: ${angle}`);

    // Remove existing event listener if any
    calibrationDataCharacteristic.removeEventListener('characteristicvaluechanged', calibrationDataCharacteristic._calibrationHandler);

    
    await writeInteger(UUIDs.calibrationControl, compassService, angle);

    // Define the handler
    function handleCalibrationData(event) {
      const buffer = new Uint8Array(event.target.value.buffer);
      const CalibrationData = protobufRoot.lookupType('compass.CalibrationData');
      const data = CalibrationData.decode(buffer);
      dataCallback(data);
    }

    // Save the new handler to the characteristic for future removal
    calibrationDataCharacteristic._calibrationHandler = handleCalibrationData;

    // Subscribe to calibration data notifications
    await calibrationDataCharacteristic.startNotifications();
    calibrationDataCharacteristic.addEventListener('characteristicvaluechanged', handleCalibrationData);

    console.log('ble.js - Calibration started successfully');
  } catch (error) {
    console.error('Error starting calibration:', error);
  }
}



export async function stopCalibration() {
  try {
    console.log('ble.js - Stopping calibration');

    // Remove existing event listener if any
    calibrationDataCharacteristic.removeEventListener('characteristicvaluechanged', calibrationDataCharacteristic._calibrationHandler);


    await writeInteger(UUIDs.calibrationControl, compassService, -1);
    await calibrationDataCharacteristic.stopNotifications();

    console.log('ble.js - Calibration stopped successfully');
  } catch (error) {
    console.error('Error stopping calibration:', error);
  }
}

export async function setFixedAngle(angle) {
  try {
    console.log(`ble.js - Setting fixed angle to: ${angle}`);
    await writeInteger(UUIDs.fixAngleControl, compassService, angle);
    console.log('ble.js - Fixed angle set successfully');
  } catch (error) {
    console.error('Error setting fixed angle:', error);
  }
}
